import React from 'react'
import { FaCheck, FaClock, FaCrown, FaPlay, FaRedo, FaTrophy } from 'react-icons/fa'

import { ColorGameState, ColorGuessing as ColorGuessingGame, ColorPickerType, RGBColor } from '../library/ColorGuessing'
import { WebRTCRole } from '../library/web-rtc/WebRTC'
import { useWebRTC } from '../library/web-rtc/useWebRTC'

export function ColorGuessing() {
  const webRTC = useWebRTC()

  switch (webRTC.role) {
    case WebRTCRole.HOST:
      return <HostColorGuessing />
    case WebRTCRole.CLIENT:
      return <ClientColorGuessing />
    default:
      return (
        <div className="flex items-center justify-center min-h-screen gradient-bg">
          <div className="text-white text-xl">Unknown role</div>
        </div>
      )
  }
}

function HostColorGuessing() {
  const webRTC = useWebRTC()
  const gameRef = React.useRef<ColorGuessingGame | null>(null)
  const [gameState, setGameState] = React.useState<ColorGameState | null>(null)

  // Initialize game
  React.useEffect(() => {
    const playerIds = [webRTC.myClientId!, ...Object.keys(webRTC.clients)]

    const game = new ColorGuessingGame(playerIds)
    gameRef.current = game

    // Listen to game state changes and broadcast to clients
    const handleStateChange = (state: ColorGameState) => {
      setGameState(state)
      webRTC.sendMessageToClients({
        type: 'color-guessing-state',
        state
      })
    }

    game.on('stateChange', handleStateChange)

    // Start configuration phase
    game.startConfiguration()

    return () => {
      game.off('stateChange', handleStateChange)
      game.destroy()
    }
  }, [])

  // Handle messages from clients
  React.useEffect(() => {
    const handleClientMessage = (event: any) => {
      const { clientId, message } = event

      if (!gameRef.current || !message || message.type !== 'color-guessing-action') {
        return
      }

      switch (message.action) {
        case 'submit-guess':
          gameRef.current.submitGuess(clientId, message.color)
          break
      }
    }

    webRTC.on('client-message', handleClientMessage)

    return () => {
      webRTC.off('client-message', handleClientMessage)
    }
  }, [webRTC])

  const handleResetGame = () => {
    if (gameRef.current) {
      const confirm = window.confirm('Are you sure you want to reset the game? All progress will be lost.')
      if (confirm) {
        gameRef.current.reset()
        gameRef.current.startConfiguration()
      }
    }
  }

  const handleStartGame = (rounds: number, timePerRound: number, pickerSequence: ColorPickerType[]) => {
    if (gameRef.current) {
      gameRef.current.submitConfiguration({
        rounds,
        timePerRound,
        colorPickerSequence: pickerSequence
      })
    }
  }

  if (!gameState) {
    return (
      <div className="flex items-center justify-center min-h-screen gradient-bg">
        <div className="text-white text-xl animate-pulse">Loading game...</div>
      </div>
    )
  }

  return <GameView gameState={gameState} webRTC={webRTC} isHost={true} gameRef={gameRef} onResetGame={handleResetGame} onStartGame={handleStartGame} />
}

function ClientColorGuessing() {
  const webRTC = useWebRTC()
  const [gameState, setGameState] = React.useState<ColorGameState | null>(null)

  // Listen to game state updates from host
  React.useEffect(() => {
    const handleHostMessage = (event: any) => {
      const { message } = event

      if (!message) return

      if (message.type === 'color-guessing-state') {
        setGameState(message.state)
      }
    }

    webRTC.on('host-message', handleHostMessage)

    return () => {
      webRTC.off('host-message', handleHostMessage)
    }
  }, [webRTC])

  if (!gameState) {
    return (
      <div className="flex items-center justify-center min-h-screen gradient-bg">
        <div className="text-white text-xl animate-pulse">Waiting for game to start...</div>
      </div>
    )
  }

  return <GameView gameState={gameState} webRTC={webRTC} isHost={false} />
}

interface GameViewProps {
  gameState: ColorGameState
  webRTC: ReturnType<typeof useWebRTC>
  isHost: boolean
  gameRef?: React.RefObject<ColorGuessingGame | null>
  onResetGame?: () => void
  onStartGame?: (rounds: number, timePerRound: number, pickerSequence: ColorPickerType[]) => void
}

function GameView({ gameState, webRTC, isHost, gameRef, onResetGame, onStartGame }: GameViewProps) {
  const myClientId = webRTC.myClientId || ''
  const myPlayer = gameState.players.find((p) => p.id === myClientId)

  // Helper to get player name
  const getPlayerName = (playerId: string): string => {
    if (playerId === myClientId) {
      return webRTC.myMetadata?.name || 'You'
    }

    if (isHost) {
      const client = webRTC.clients[playerId]
      return client?.metadata?.name || 'Unknown'
    } else {
      // Check if this is the host (host's clientId is empty string or not in our client lists)
      const isHostPlayer = !webRTC.meshClients[playerId] && !Object.keys(webRTC.meshClients).includes(playerId) && playerId !== myClientId
      if (isHostPlayer && webRTC.host) {
        return webRTC.host?.metadata?.name || 'Host'
      }
      const meshClient = webRTC.meshClients[playerId]
      return meshClient?.metadata?.name || 'Unknown'
    }
  }

  const handleColorClick = (color: RGBColor) => {
    if (myPlayer?.hasSubmitted) return

    if (isHost && gameRef?.current) {
      gameRef.current.submitGuess(myClientId, color)
    } else {
      webRTC.sendMessageToHost({
        type: 'color-guessing-action',
        action: 'submit-guess',
        color
      })
    }
  }

  // Format time remaining
  const formatTime = (ms: number | null): string => {
    if (ms === null) return '--:--'
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen gradient-bg">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-4 md:py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-2 sm:mb-4 md:mb-8 animate-slide-up">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg">
            🎨 <span className="rainbow-gradient-text">Shade Parades</span>
          </h1>
          {isHost && onResetGame && gameState.phase !== 'configuring' && (
            <button
              onClick={onResetGame}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1 sm:py-2 text-sm sm:text-base bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-all duration-300 hover:scale-105 active:scale-95"
            >
              <FaRedo className="text-xs sm:text-base" /> <span className="hidden sm:inline">Reset</span>
            </button>
          )}
        </div>

        {/* Configuration Phase */}
        {gameState.phase === 'configuring' && <ConfigurationView isHost={isHost} onStartGame={onStartGame} />}

        {/* Waiting Phase */}
        {gameState.phase === 'waiting' && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="card animate-scale-in text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Get Ready!</h2>
              <p className="text-gray-600">The game will start soon...</p>
            </div>
          </div>
        )}

        {/* Round Starting Phase */}
        {gameState.phase === 'round_starting' && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="card animate-scale-in text-center">
              <h2 className="text-4xl font-bold rainbow-gradient-text mb-4">Round {gameState.currentRound}</h2>
            </div>
          </div>
        )}

        {/* Guessing Phase */}
        {gameState.phase === 'guessing' && (
          <div className="space-y-2 sm:space-y-3 md:space-y-6 animate-fade-in">
            {/* Status Bar */}
            <div className="card !p-2 sm:!p-4 md:!p-6">
              <div className="flex flex-wrap justify-between items-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm md:text-base">
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="text-gray-600 font-medium hidden sm:inline">Round:</span>
                  <span className="text-gray-600 font-medium sm:hidden">R:</span>
                  <span className="text-lg sm:text-xl md:text-2xl font-bold rainbow-gradient-text">
                    {gameState.currentRound}/{gameState.totalRounds}
                  </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="text-gray-600 font-medium hidden sm:inline">Turn:</span>
                  <span className="text-gray-600 font-medium sm:hidden">T:</span>
                  <span className="text-base sm:text-lg md:text-xl font-bold text-gray-800">
                    {gameState.currentTurnInRound + 1}/{gameState.totalTurnsPerRound}
                  </span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <FaClock className="rainbow-gradient-text text-sm sm:text-base" />
                  <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">{formatTime(gameState.timeRemaining)}</span>
                </div>
                <div className="text-xs sm:text-sm text-gray-600 capitalize hidden md:block">{gameState.currentColorPickerType?.replace('_', ' ')} Picker</div>
              </div>
            </div>

            {/* Color Matching Area - Side by Side */}
            <div className="grid md:grid-cols-2 gap-2 sm:gap-3 md:gap-6">
              {/* Target Color Display */}
              <div className="card !p-2 sm:!p-4 md:!p-6">
                <h2 className="text-sm sm:text-lg md:text-2xl font-bold text-gray-800 mb-1 sm:mb-2 md:mb-3 text-center">🎯 Target Color</h2>
                <p className="text-center text-gray-600 mb-1 sm:mb-2 text-xs sm:text-sm hidden sm:block">Match this color as closely as possible!</p>
                {gameState.targetColor ? (
                  <>
                    <div
                      className="w-full rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl md:shadow-2xl border-2 sm:border-3 md:border-4 border-white"
                      style={{
                        backgroundColor: rgbToHex(gameState.targetColor),
                        aspectRatio: '1',
                        maxHeight: 'min(40vh, 400px)',
                        minHeight: '150px'
                      }}
                    />
                    <p className="text-center mt-1 sm:mt-2 md:mt-3 text-gray-600 font-mono text-xs sm:text-sm md:text-base font-bold">{rgbToHex(gameState.targetColor)}</p>
                  </>
                ) : (
                  <div
                    className="w-full rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl md:shadow-2xl border-2 sm:border-3 md:border-4 border-gray-300 bg-gray-100 flex items-center justify-center"
                    style={{
                      aspectRatio: '1',
                      maxHeight: 'min(40vh, 400px)',
                      minHeight: '150px'
                    }}
                  >
                    <p className="text-gray-400 text-sm sm:text-base md:text-lg">Loading...</p>
                  </div>
                )}
              </div>

              {/* Color Picker */}
              <div className="card !p-2 sm:!p-4 md:!p-6">
                {myPlayer?.hasSubmitted ? (
                  <div className="text-center py-2 sm:py-4 md:py-6">
                    <div className="inline-block p-2 sm:p-3 md:p-4 bg-green-100 rounded-full mb-2 sm:mb-3 md:mb-4 animate-bounce-subtle">
                      <FaCheck className="text-xl sm:text-2xl md:text-3xl text-green-600" />
                    </div>
                    <h3 className="text-sm sm:text-base md:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Color Submitted!</h3>
                    <p className="text-gray-600 mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm">Waiting for others...</p>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 font-semibold">Your Guess:</p>
                      <div
                        className="mx-auto rounded-lg sm:rounded-xl md:rounded-2xl shadow-md sm:shadow-lg md:shadow-xl border-2 sm:border-3 md:border-4 border-gray-200"
                        style={{
                          backgroundColor: rgbToHex(myPlayer.currentGuess!),
                          width: 'min(60%, 200px)',
                          aspectRatio: '1',
                          maxHeight: 'min(25vh, 200px)',
                          minHeight: '100px'
                        }}
                      />
                      <p className="text-center mt-1 sm:mt-2 text-gray-500 font-mono text-xs sm:text-sm md:text-base font-bold">{rgbToHex(myPlayer.currentGuess!)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-sm sm:text-lg md:text-2xl font-bold text-gray-800 mb-1 sm:mb-2 md:mb-3 text-center">🎨 Your Color Picker</h2>
                    <p className="text-center text-gray-600 mb-1 sm:mb-2 text-xs sm:text-sm hidden sm:block">Click anywhere to submit your guess!</p>
                    <ColorPicker type={gameState.currentColorPickerType!} onColorSelect={handleColorClick} baseHue={gameState.pickerBaseHue} />
                  </>
                )}
              </div>
            </div>

            {/* Players Status */}
            <div className="card !p-2 sm:!p-4 md:!p-6">
              <h3 className="text-sm sm:text-base md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">Players</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-1 sm:gap-2 md:gap-4">
                {gameState.players.map((player) => (
                  <div
                    key={player.id}
                    className={`p-1.5 sm:p-2 md:p-4 rounded-md sm:rounded-lg transition-all duration-300 ${
                      player.hasSubmitted ? 'bg-green-100 border border-green-500 sm:border-2' : 'bg-gray-100 border border-gray-300 sm:border-2'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-0.5 sm:mb-1 md:mb-2">
                      <span className="font-semibold text-gray-800 truncate text-xs sm:text-sm md:text-base">{getPlayerName(player.id)}</span>
                      {player.hasSubmitted && <FaCheck className="text-green-600 text-xs sm:text-sm md:text-base flex-shrink-0 ml-1" />}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      <span className="hidden sm:inline">Score: </span>
                      {player.score.toFixed(player.score >= 100 ? 0 : 1)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Turn Ended Phase */}
        {gameState.phase === 'turn_ended' && gameState.roundResults && <RoundResultsView gameState={gameState} getPlayerName={getPlayerName} myClientId={myClientId} />}

        {/* Round Ended Phase */}
        {gameState.phase === 'round_ended' && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="card animate-scale-in text-center">
              <h2 className="text-4xl font-bold rainbow-gradient-text mb-4">Round {gameState.currentRound} Complete!</h2>
              <p className="text-xl text-gray-700 mb-2">Starting next round...</p>
            </div>
          </div>
        )}

        {/* Game Ended Phase */}
        {gameState.phase === 'game_ended' && <GameEndView gameState={gameState} getPlayerName={getPlayerName} isHost={isHost} onResetGame={onResetGame} />}
      </div>
    </div>
  )
}

interface ConfigurationViewProps {
  isHost: boolean
  onStartGame?: (rounds: number, timePerRound: number, pickerSequence: ColorPickerType[]) => void
}

function ConfigurationView({ isHost, onStartGame }: ConfigurationViewProps) {
  const [rounds, setRounds] = React.useState(3)
  const [timePerRound, setTimePerRound] = React.useState(30)
  const [pickerMode, setPickerMode] = React.useState<'sequential' | 'random'>('random')
  const [pickerSequence, setPickerSequence] = React.useState<ColorPickerType[]>(['basic', 'wheel', 'saturation'])

  const pickerTypes: ColorPickerType[] = ['basic', 'wheel', 'saturation', 'hue_slider', 'monochrome']

  const handleStart = () => {
    if (onStartGame) {
      const sequence = pickerMode === 'random' ? Array(rounds).fill('random' as ColorPickerType) : pickerSequence.slice(0, rounds)
      onStartGame(rounds, timePerRound * 1000, sequence)
    }
  }

  return (
    <div className="max-w-2xl mx-auto animate-scale-in">
      <div className="card">
        <h2 className="text-3xl font-bold text-center mb-6 rainbow-gradient-text">Game Configuration</h2>

        {isHost ? (
          <div className="space-y-6">
            {/* Rounds */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Number of Rounds</label>
              <input
                type="range"
                min="1"
                max="10"
                value={rounds}
                onChange={(e) => setRounds(Number(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="text-center text-2xl font-bold rainbow-gradient-text mt-2">{rounds}</div>
            </div>

            {/* Time per Round */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Time per Round (seconds)</label>
              <input
                type="range"
                min="15"
                max="120"
                step="5"
                value={timePerRound}
                onChange={(e) => setTimePerRound(Number(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="text-center text-2xl font-bold rainbow-gradient-text mt-2">{timePerRound}s</div>
            </div>

            {/* Picker Mode */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Color Picker Mode</label>
              <div className="flex gap-4">
                <button
                  onClick={() => setPickerMode('random')}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                    pickerMode === 'random' ? 'rainbow-gradient text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Random
                </button>
                <button
                  onClick={() => setPickerMode('sequential')}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                    pickerMode === 'sequential' ? 'rainbow-gradient text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Sequential
                </button>
              </div>
            </div>

            {/* Sequential Picker Selection */}
            {pickerMode === 'sequential' && (
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Select Picker Sequence</label>
                <div className="grid grid-cols-2 gap-3">
                  {pickerTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        const index = pickerSequence.indexOf(type)
                        if (index >= 0) {
                          setPickerSequence(pickerSequence.filter((t) => t !== type))
                        } else {
                          setPickerSequence([...pickerSequence, type])
                        }
                      }}
                      className={`py-2 px-4 rounded-lg font-medium transition-all duration-300 capitalize ${
                        pickerSequence.includes(type) ? 'rainbow-gradient text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {type.replace('_', ' ')}
                    </button>
                  ))}
                </div>
                {pickerSequence.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Order: {pickerSequence.map((t) => t.replace('_', ' ')).join(' → ')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Start Button */}
            <button onClick={handleStart} className="w-full flex items-center justify-center gap-3 btn-primary text-lg py-4">
              <FaPlay /> Start Game
            </button>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">Waiting for host to configure the game...</p>
            <div className="mt-4 animate-pulse">
              <div className="inline-block w-16 h-16 rounded-full animate-spin rainbow-spinner"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface ColorPickerProps {
  type: ColorPickerType
  onColorSelect: (color: RGBColor) => void
  baseHue?: number | null
}

function ColorPicker({ type, onColorSelect, baseHue }: ColorPickerProps) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const size = canvas.width

    switch (type) {
      case 'basic':
        drawBasicPicker(ctx, size)
        break
      case 'wheel':
        drawWheelPicker(ctx, size)
        break
      case 'saturation':
        drawSaturationPicker(ctx, size, baseHue ?? undefined)
        break
      case 'hue_slider':
        drawHueSliderPicker(ctx, size)
        break
      case 'monochrome':
        drawMonochromePicker(ctx, size)
        break
    }
  }, [type, baseHue])

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = Math.floor((e.clientX - rect.left) * (canvas.width / rect.width))
    const y = Math.floor((e.clientY - rect.top) * (canvas.height / rect.height))

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const pixel = ctx.getImageData(x, y, 1, 1).data
    onColorSelect({ r: pixel[0], g: pixel[1], b: pixel[2] })
  }

  return (
    <div className="color-picker-container">
      <canvas ref={canvasRef} width={400} height={400} onClick={handleCanvasClick} className="w-full h-full" />
    </div>
  )
}

// Color Picker Drawing Functions
function drawBasicPicker(ctx: CanvasRenderingContext2D, size: number) {
  for (let x = 0; x < size; x++) {
    for (let y = 0; y < size; y++) {
      const r = Math.floor((x / size) * 255)
      const g = Math.floor((y / size) * 255)
      const b = 128
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
      ctx.fillRect(x, y, 1, 1)
    }
  }
}

function drawWheelPicker(ctx: CanvasRenderingContext2D, size: number) {
  const centerX = size / 2
  const centerY = size / 2
  const radius = size / 2

  for (let x = 0; x < size; x++) {
    for (let y = 0; y < size; y++) {
      const dx = x - centerX
      const dy = y - centerY
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance <= radius) {
        const angle = Math.atan2(dy, dx)
        const hue = ((angle + Math.PI) / (2 * Math.PI)) * 360
        const saturation = (distance / radius) * 100
        const lightness = 50

        ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`
        ctx.fillRect(x, y, 1, 1)
      } else {
        ctx.fillStyle = '#f0f0f0'
        ctx.fillRect(x, y, 1, 1)
      }
    }
  }
}

function drawSaturationPicker(ctx: CanvasRenderingContext2D, size: number, baseHue?: number) {
  // Use provided base hue or generate random one as fallback
  const hue = baseHue ?? Math.floor(Math.random() * 360)

  for (let x = 0; x < size; x++) {
    for (let y = 0; y < size; y++) {
      const saturation = (x / size) * 100
      const lightness = (y / size) * 100

      ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`
      ctx.fillRect(x, y, 1, 1)
    }
  }
}

function drawHueSliderPicker(ctx: CanvasRenderingContext2D, size: number) {
  for (let x = 0; x < size; x++) {
    for (let y = 0; y < size; y++) {
      const hue = (x / size) * 360
      const lightness = 50 + (y / size) * 50

      ctx.fillStyle = `hsl(${hue}, 100%, ${lightness}%)`
      ctx.fillRect(x, y, 1, 1)
    }
  }
}

function drawMonochromePicker(ctx: CanvasRenderingContext2D, size: number) {
  for (let x = 0; x < size; x++) {
    for (let y = 0; y < size; y++) {
      const gray = Math.floor((x / size) * 255)
      const tint = Math.floor((y / size) * 128)

      ctx.fillStyle = `rgb(${gray}, ${gray + tint}, ${gray})`
      ctx.fillRect(x, y, 1, 1)
    }
  }
}

interface RoundResultsViewProps {
  gameState: ColorGameState
  getPlayerName: (id: string) => string
  myClientId: string
}

function RoundResultsView({ gameState, getPlayerName, myClientId }: RoundResultsViewProps) {
  return (
    <div className="space-y-2 sm:space-y-3 md:space-y-6 animate-fade-in">
      {/* Target Color */}
      <div className="card text-center !p-2 sm:!p-4 md:!p-6">
        <h2 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4">
          Turn {gameState.currentTurnInRound + 1} Results - Round {gameState.currentRound}
        </h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-2 sm:mb-3 md:mb-4">The target color was:</p>
        <div
          className="mx-auto rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl md:shadow-2xl animate-pulse-slow"
          style={{
            backgroundColor: rgbToHex(gameState.targetColor!),
            width: 'min(50%, 200px)',
            aspectRatio: '1',
            maxHeight: 'min(30vh, 200px)',
            minHeight: '120px'
          }}
        />
        <p className="mt-2 sm:mt-3 md:mt-4 text-xs sm:text-sm text-gray-500 font-mono">{rgbToHex(gameState.targetColor!)}</p>
      </div>

      {/* Results Grid */}
      <div className="grid md:grid-cols-2 gap-2 sm:gap-3 md:gap-6">
        {gameState
          .roundResults!.sort((a, b) => b.points - a.points)
          .map((result, index) => {
            const isMe = result.playerId === myClientId
            return (
              <div
                key={result.playerId}
                className={`card ${isMe ? 'ring-2 sm:ring-4' : ''} animate-slide-up !p-2 sm:!p-3 md:!p-4`}
                style={{ animationDelay: `${index * 0.1}s`, ...(isMe ? { boxShadow: '0 0 0 2px #E40303' } : {}) }}
              >
                <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
                  <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
                    {index === 0 && <FaTrophy className="text-yellow-500 text-base sm:text-xl md:text-2xl" />}
                    <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800">
                      {getPlayerName(result.playerId)}
                      {isMe && ' (You)'}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold rainbow-gradient-text">+{result.points.toFixed(2)}</div>
                    <div className="text-xs sm:text-sm text-gray-500">{(result.accuracy * 100).toFixed(1)}%</div>
                  </div>
                </div>

                {/* Color Comparison */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 mb-2 sm:mb-3">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-center">Your Guess</p>
                    <div
                      className="w-full rounded-md sm:rounded-lg shadow-md"
                      style={{
                        backgroundColor: rgbToHex(result.guess),
                        height: 'min(15vw, 80px)',
                        minHeight: '60px'
                      }}
                    />
                    <p className="text-xs text-gray-500 text-center mt-1 font-mono">{rgbToHex(result.guess)}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 text-center">Target</p>
                    <div
                      className="w-full rounded-md sm:rounded-lg shadow-md"
                      style={{
                        backgroundColor: rgbToHex(gameState.targetColor!),
                        height: 'min(15vw, 80px)',
                        minHeight: '60px'
                      }}
                    />
                    <p className="text-xs text-gray-500 text-center mt-1 font-mono">{rgbToHex(gameState.targetColor!)}</p>
                  </div>
                </div>

                {/* Accuracy Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div className="h-full rainbow-gradient transition-all duration-1000" style={{ width: `${result.accuracy * 100}%` }} />
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

interface GameEndViewProps {
  gameState: ColorGameState
  getPlayerName: (id: string) => string
  isHost: boolean
  onResetGame?: () => void
}

function GameEndView({ gameState, getPlayerName, isHost, onResetGame }: GameEndViewProps) {
  const leaderboard = [...gameState.players].sort((a, b) => b.score - a.score)
  const winner = leaderboard[0]

  return (
    <div className="space-y-6 animate-scale-in">
      {/* Winner Announcement */}
      <div className="card text-center">
        <div className="inline-block p-6 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full mb-4">
          <FaCrown className="text-6xl text-white" />
        </div>
        <h1 className="text-5xl font-bold rainbow-gradient-text mb-2">Game Over!</h1>
        <h2 className="text-3xl font-bold text-gray-800 mb-4">{getPlayerName(winner.id)} Wins!</h2>
        <p className="text-2xl text-gray-600">
          Final Score: <span className="font-bold rainbow-gradient-text">{winner.score.toFixed(2)}</span> points
        </p>
      </div>

      {/* Final Leaderboard */}
      <div className="card">
        <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <FaTrophy className="text-yellow-500" />
          Final Leaderboard
        </h2>
        <div className="space-y-4">
          {leaderboard.map((player, index) => (
            <div
              key={player.id}
              className={`flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${
                index === 0
                  ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 shadow-lg'
                  : index === 1
                    ? 'bg-gradient-to-r from-gray-100 to-gray-200'
                    : index === 2
                      ? 'bg-gradient-to-r from-orange-100 to-orange-200'
                      : 'bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`text-3xl font-bold ${index === 0 ? 'text-yellow-600' : index === 1 ? 'text-gray-600' : index === 2 ? 'text-orange-600' : 'text-gray-400'}`}>
                  #{index + 1}
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-800">{getPlayerName(player.id)}</div>
                </div>
              </div>
              <div className="text-2xl font-bold rainbow-gradient-text">{player.score.toFixed(2)}</div>
            </div>
          ))}
        </div>

        {isHost && onResetGame && (
          <button onClick={onResetGame} className="w-full mt-6 btn-primary text-lg py-4 flex items-center justify-center gap-3">
            <FaRedo /> Play Again
          </button>
        )}
      </div>
    </div>
  )
}

// Helper function
function rgbToHex(color: RGBColor): string {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`
}
